import { useState, useRef, useEffect } from 'react';
import { useDirectMessageStore } from '../../stores/directMessageStore.js';
import { useSocketEmit } from '../../hooks/useSocket.js';
import { useAeThex } from '../../components/AeThexProvider';
import { X } from 'lucide-react';

export default function DirectMessageChat() {
  const { getCurrentConversation, addMessage, setCurrentConversation } = useDirectMessageStore();
  const { sendDirectMessage, emitTyping } = useSocketEmit();
  const { user } = useAeThex();
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const conversation = getCurrentConversation();
  const myId = user?.id || 'user-1';

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages?.length]);

  if (!conversation) {
    return (
      <div style={{ flex: 1, background: 'rgb(var(--aethex-bg))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>💬</div>
          <p style={{ color: 'rgb(var(--aethex-dim))', fontSize: 14 }}>Select a conversation to start messaging</p>
        </div>
      </div>
    );
  }

  const handleSend = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending) return;

    setSending(true);
    const newMessage = {
      id: `msg-${Date.now()}`,
      senderId: myId,
      text: messageText.trim(),
      timestamp: Date.now(),
    };

    addMessage(conversation.id, newMessage);
    sendDirectMessage(conversation.id, messageText.trim(), myId);
    setMessageText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleTyping = (e) => {
    const val = e.target.value;
    setMessageText(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
    emitTyping(conversation.id, myId, user?.username || 'You');
  };

  return (
    <div style={{ flex: 1, background: 'rgb(var(--aethex-bg))', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid rgb(var(--aethex-border))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgb(var(--aethex-panel))', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, rgb(var(--aethex-primary)/0.8), rgb(var(--aethex-primary)/0.4))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
            {conversation.avatar}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'rgb(var(--aethex-text))' }}>{conversation.userName}</div>
            <div style={{ fontSize: 11, color: 'rgb(var(--aethex-dim))' }}>Direct Message</div>
          </div>
        </div>
        <button
          onClick={() => setCurrentConversation(null)}
          style={{ background: 'none', border: 'none', color: 'rgb(var(--aethex-dim))', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }}
          title="Close conversation"
        >
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {conversation.messages.map((msg) => {
          const isOwn = msg.senderId === myId;
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '70%',
                padding: '8px 14px',
                borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: isOwn ? 'rgb(var(--aethex-primary)/0.25)' : 'rgb(var(--aethex-surface))',
                border: `1px solid ${isOwn ? 'rgb(var(--aethex-primary)/0.3)' : 'rgb(var(--aethex-border))'}`,
              }}>
                <p style={{ fontSize: 14, color: 'rgb(var(--aethex-text))', margin: 0, lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.text}</p>
                <p style={{ fontSize: 10, color: 'rgb(var(--aethex-dim))', margin: '4px 0 0', textAlign: isOwn ? 'right' : 'left' }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid rgb(var(--aethex-border))', flexShrink: 0 }}>
        <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleTyping}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${conversation.userName}...`}
            disabled={sending}
            rows={1}
            style={{
              flex: 1,
              background: 'rgb(var(--aethex-surface))',
              border: '1px solid rgb(var(--aethex-border))',
              borderRadius: 8,
              padding: '8px 14px',
              color: 'rgb(var(--aethex-text))',
              fontSize: 14,
              resize: 'none',
              overflowY: 'auto',
              minHeight: 36,
              maxHeight: 150,
              outline: 'none',
              fontFamily: 'inherit',
              lineHeight: 1.5,
            }}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            style={{
              background: messageText.trim() ? 'rgb(var(--aethex-primary)/0.8)' : 'rgb(var(--aethex-surface))',
              border: '1px solid rgb(var(--aethex-border))',
              color: messageText.trim() ? '#fff' : 'rgb(var(--aethex-dim))',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: messageText.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 600,
              fontSize: 13,
              flexShrink: 0,
              height: 36,
              transition: 'all 0.15s',
            }}
          >
            {sending ? '⏳' : '➤'}
          </button>
        </form>
      </div>
    </div>
  );
}
